import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * NexusAdminProvider — context plumbing for the admin-ui package.
 *
 * The client SPA owns auth + URL routing. We just collect two values:
 *
 *   - apiBase:       base URL prefix for Nexus admin REST endpoints.
 *                    Pages do `nexusFetch('/admin/broker-creds')` and the
 *                    provider concatenates → '/api/admin/broker-creds'.
 *
 *   - getAuthToken:  pluggable function returning the current bearer
 *                    token (or null when signed out). Called fresh on
 *                    every fetch so token rotation Just Works.
 *
 * Default getAuthToken is `() => null` so unauthenticated pages still
 * render — calls then hit the API anonymously and the server returns
 * the appropriate 401, which the client SPA surfaces.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, } from 'react';
import { StepUpDialog } from './components/StepUpDialog.js';
const NexusAdminContext = createContext(null);
export function NexusAdminProvider({ apiBase, getAuthToken, userEmail, onSignOut, onAdminMfaRequired, onAccessToken, refreshAccessToken, stepUpDialog, children, }) {
    const dialogOn = stepUpDialog ?? !!onAccessToken;
    const [stepUpOpen, setStepUpOpen] = useState(false);
    const waiting = useRef([]);
    const settle = useCallback((ok) => {
        const list = waiting.current;
        waiting.current = [];
        setStepUpOpen(false);
        for (const resolve of list)
            resolve(ok);
    }, []);
    const requestStepUp = useCallback(() => new Promise((resolve) => {
        waiting.current.push(resolve);
        setStepUpOpen(true);
    }), []);
    const value = useMemo(() => ({
        apiBase: apiBase.replace(/\/+$/, ''),
        getAuthToken: getAuthToken ?? (() => null),
        userEmail: userEmail ?? null,
        onSignOut,
        onAdminMfaRequired,
        onAccessToken,
        refreshAccessToken,
        ...(dialogOn ? { requestStepUp } : {}),
    }), [apiBase, getAuthToken, userEmail, onSignOut, onAdminMfaRequired, onAccessToken, refreshAccessToken, dialogOn, requestStepUp]);
    return (_jsxs(NexusAdminContext.Provider, { value: value, children: [children, dialogOn && stepUpOpen && _jsx(StepUpDialog, { onDone: () => settle(true), onCancel: () => settle(false) })] }));
}
/**
 * Read the admin-ui context. Throws if the caller is rendered outside
 * <NexusAdminProvider> — a programmer error worth surfacing loudly.
 */
export function useNexusAdmin() {
    const ctx = useContext(NexusAdminContext);
    if (!ctx) {
        throw new Error('useNexusAdmin must be used inside <NexusAdminProvider>. ' +
            'Wrap your app or admin route subtree in <NexusAdminProvider apiBase=... getAuthToken=...>.');
    }
    return ctx;
}
//# sourceMappingURL=provider.js.map